import React from 'react';
import { Button } from '@shadcn/ui/components/ui/button';
import { PanelRight, AlarmClock, ChevronLeft, CalendarDays, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@shadcn/ui/components/ui/dropdown-menu';

import { KeyboardSymbol, OSMetaKey } from '@shadcn/ui/components/keyboard-symbol';
import { Skeleton } from '@shadcn/ui/components/ui/skeleton';
import { cn } from '@shadcn/ui/lib/utils';
import { useIsMobile } from '@shadcn/ui/hooks/use-mobile';
import { GetContactAndConversationByIdQuery } from '@zaplify/graphql';
import { ConnectionStatusBadgeV2 } from '../../pages/actions/components/connection-status-badge';
import { useSnoozeDialogV2 } from '../dialogs/snooze-dialog-v2';
import { PlaybookBadge } from '../playbook-badge';
import { QuickSnoozeDropdown } from '../buttons';

interface ActionsPageHeaderProps {
    onToggleDetails: () => void;
    isDetailsOpen: boolean;
    hideSectionPart?: boolean;
    forceShowBackButton?: boolean;
    contact: GetContactAndConversationByIdQuery['Contacts_by_pk'];
}

export const ConversationPageHeaderSkeleton: React.FC = () => {
    const location = useLocation();
    const isOnActionsPage = location.pathname.includes('actions');
    return (
        <header className="sticky top-0 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex h-14 items-center justify-between gap-4 px-4">
                {/* Left section */}
                <div className="flex items-center gap-4 w-[304px] border-r h-full pr-4">
                    <span className="font-medium text-2xl">{isOnActionsPage ? 'Actions' : 'Conversations'}</span>
                    <Skeleton className="w-8 h-8 rounded-full bg-background-brand-primary-subtle" />
                </div>
                {/* Center section */}
                <div className="flex flex-1 items-center justify-between pl-4">
                    <div className="flex items-center gap-3 flex-grow">
                        <Skeleton className="w-48 h-2 bg-background-brand-primary-subtle" />
                    </div>

                    {/* Right section */}
                    <div className="flex items-center gap-4">
                        {isOnActionsPage && (
                            <Skeleton className="w-16 h-8 rounded-md bg-background-brand-primary-subtle" />
                        )}
                        <Skeleton className="w-16 h-8 rounded-md bg-background-brand-primary-subtle" />
                    </div>
                </div>
            </div>
        </header>
    );
};

export const ConversationPageHeader: React.FC<ActionsPageHeaderProps> = ({
    onToggleDetails,
    isDetailsOpen,
    contact,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    return (
        <header className="sticky top-0 w-full min-w-full h-14 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex h-14 items-center justify-between gap-4 px-4">
                <div className="flex flex-1 gap-4 items-center justify-between w-full">
                    <Button
                        variant="ghost"
                        size="icon"
                        className={cn('flex items-center gap-2')}
                        onClick={() => {
                            const { pathname, search } = window.location;
                            const newPath = pathname.split('/').slice(0, -2).join('/');
                            navigate(`${newPath}${search}`);
                        }}
                    >
                        <X className="h-6 w-6" />
                    </Button>
                    <div className="flex items-center gap-3">
                        {!isDetailsOpen && !isMobile ? (
                            <div className="flex flex-col gap-0">
                                <div className="flex gap-2 items-center">
                                    <span className="font-medium text-lg line-clamp-1 text-nowrap capitalize">
                                        {contact.fullName}
                                    </span>
                                    <ConnectionStatusBadgeV2 key={contact.id} contact={contact} />
                                    {contact.prospectId && <PlaybookBadge prospectId={contact.prospectId} />}
                                </div>
                                <span className="text-xs text-text-tertiary line-clamp-1 capitalize">
                                    {contact.occupationTitle
                                        ? `${contact.occupationTitle} - ${contact.organizationName}`
                                        : contact.linkedinHeadline}
                                </span>
                            </div>
                        ) : (
                            <div className="flex flex-1 items-center gap-3">
                                <span className="font-medium text-base md:text-lg line-clamp-1 text-nowrap capitalize">
                                    {contact.fullName}
                                </span>
                                <ConnectionStatusBadgeV2 key={contact.id} contact={contact} />
                            </div>
                        )}
                    </div>
                    {!isMobile && <div className="flex-1" />}
                    <div className="flex items-center gap-2 justify-end">
                        <QuickSnoozeDropdown
                            contactFirstName={contact.firstName}
                            contactId={contact.id}
                            contact={contact}
                            variant="outline"
                            size="sm"
                            showLabel
                            showStopSuggestingActions
                            className="border border-input"
                        />

                        {!isDetailsOpen && !isMobile && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex items-center gap-2 animate-in fade-in-0 zoom-in-95"
                                            onClick={onToggleDetails}
                                        >
                                            <PanelRight className="h-4 w-4" />
                                            <span className="hidden xl:inline">View Details</span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Open contact details{' '}
                                        <KeyboardSymbol>
                                            <OSMetaKey />
                                            +i
                                        </KeyboardSymbol>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        {/* {isMobile && isOnActionsPage && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" size="sm" className="flex items-center gap-2">
                                            <DotsHorizontalIcon className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        <DropdownMenuItem onClick={onToggleDetails}>
                                            <UserRound className="h-4 w-4" />
                                            <span>View Details</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openSnoozeDialog({ prospectId })}>
                                            <AlarmClock className="h-4 w-4" />
                                            <span>Snooze Action</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )} */}
                    </div>
                </div>
            </div>
        </header>
    );
};
